import {
  Activity,
  AlertTriangle,
  ArrowRight,
  CalendarClock,
  CheckCircle2,
  Coins,
  Gauge,
  Gift,
  RefreshCw,
  Send,
  ShieldCheck,
  UsersRound,
  WalletCards,
} from "lucide-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState, type SyntheticEvent } from "react";
import {
  type TenantRewardCampaign,
  type TenantRewardCampaignStatus,
  type TenantRewardReferralInput,
  type TenantRewardReferralStatus,
  type TenantCustomerSummary,
  createTenantRewardReferral,
  fetchTenantCustomers,
  fetchTenantRewardCampaigns,
  fetchTenantRewardReferrals,
  messageFromError,
  transitionTenantRewardCampaign,
  updateTenantRewardCampaignExecutionLimits,
} from "../api";
import { AppAlert } from "../components/AppAlertDialog";
import "./TenantCampaignExecutionPanel.css";

export const tenantCampaignExecutionKey = ["tenant-reward-campaigns"] as const;
export const tenantReferralKey = ["tenant-reward-referrals"] as const;

const tenantRewardAuditKey = ["tenant-reward-audit"] as const;
const tenantRewardCalendarKey = ["tenant-reward-calendar"] as const;
const tenantRewardDashboardKey = ["tenant-reward-dashboard"] as const;

const transitions: Record<TenantRewardCampaignStatus, readonly TenantRewardCampaignStatus[]> = {
  DRAFT: ["PENDING_APPROVAL", "APPROVED", "ARCHIVED"],
  PENDING_APPROVAL: ["APPROVED", "REJECTED", "ARCHIVED"],
  APPROVED: ["SCHEDULED", "ACTIVE", "ARCHIVED"],
  SCHEDULED: ["ACTIVE", "PAUSED", "COMPLETED", "ARCHIVED"],
  ACTIVE: ["PAUSED", "COMPLETED"],
  PAUSED: ["SCHEDULED", "ACTIVE", "COMPLETED", "ARCHIVED"],
  COMPLETED: ["ARCHIVED"],
  REJECTED: ["DRAFT", "ARCHIVED"],
  ARCHIVED: [],
};

export const campaignLifecycleTargets = (status: TenantRewardCampaignStatus) => transitions[status];

export function campaignExecutionMetrics(campaign: Pick<
  TenantRewardCampaign,
  "issuedCount" | "redeemedCount" | "quantityLimit" | "budgetMinor" | "budgetCommittedMinor" | "budgetRedeemedMinor"
>) {
  const exposureMinor = campaign.budgetCommittedMinor + campaign.budgetRedeemedMinor;
  const issuePercent = campaign.quantityLimit && campaign.quantityLimit > 0
    ? Math.min(100, (campaign.issuedCount / campaign.quantityLimit) * 100)
    : null;
  const budgetPercent = campaign.budgetMinor && campaign.budgetMinor > 0
    ? Math.min(100, (exposureMinor / campaign.budgetMinor) * 100)
    : null;
  const redemptionPercent = campaign.issuedCount > 0
    ? Math.min(100, (campaign.redeemedCount / campaign.issuedCount) * 100)
    : 0;
  return { exposureMinor, issuePercent, budgetPercent, redemptionPercent };
}

const titleCase = (value: string) => value
  .toLowerCase()
  .replaceAll("_", " ")
  .replace(/\b\w/g, (letter) => letter.toUpperCase());

const formatNumber = (value: number) => new Intl.NumberFormat("en-US").format(value);

const formatMoney = (minor: number, currencyCode: string) => new Intl.NumberFormat("en", {
  style: "currency",
  currency: currencyCode,
  maximumFractionDigits: 2,
}).format(minor / 100);

const formatDate = (value: string | null, includeTime = false) => {
  if (!value) return "Not set";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Not set";
  return new Intl.DateTimeFormat("en", includeTime
    ? { dateStyle: "medium", timeStyle: "short" }
    : { dateStyle: "medium" }).format(date);
};

const memberName = (firstName: string, lastName: string) =>
  `${firstName} ${lastName}`.trim() || "Member";

const createIdempotencyKey = () => {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) return crypto.randomUUID();
  return `referral-${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
};

const allReferralStatuses = [
  "PENDING",
  "QUALIFIED",
  "REWARDED",
  "REJECTED",
  "CANCELLED",
] as const;

function StatusBadge({ status }: { status: string }) {
  const token = status.toLowerCase().replace(/[^a-z0-9_-]/g, "-");
  return <span className={`campaign-execution-status campaign-execution-status--${token}`}>{titleCase(status)}</span>;
}

function QueryState({
  label,
  error,
  retry,
}: {
  label?: string;
  error?: unknown;
  retry?: () => void;
}) {
  if (error !== undefined) {
    return <><AppAlert message={messageFromError(error)} /><div className="campaign-execution-state campaign-execution-state--error">
      <AlertTriangle aria-hidden="true" />
      <div><strong>Unable to load this section</strong><p>Try loading it again.</p></div>
      {retry && <button className="button button--outline button--compact" type="button" onClick={retry}>Try again</button>}
    </div></>;
  }
  return <div className="campaign-execution-state" role="status">
    <Activity className="spinner" aria-hidden="true" />
    <strong>{label ?? "Loading"}</strong>
  </div>;
}

function ExecutionProgress({ label, value, detail }: { label: string; value: number | null; detail: string }) {
  return <div className="campaign-execution-progress">
    <div><span>{label}</span><strong>{value === null ? "No cap" : `${value.toFixed(1)}%`}</strong></div>
    <div
      aria-label={`${label}: ${detail}`}
      aria-valuemax={value === null ? undefined : 100}
      aria-valuemin={value === null ? undefined : 0}
      aria-valuenow={value === null ? undefined : Math.round(value)}
      className="campaign-execution-progress__track"
      role={value === null ? undefined : "progressbar"}
    >
      <span style={{ width: `${String(value ?? 0)}%` }} />
    </div>
    <small>{detail}</small>
  </div>;
}

function CampaignControls({
  campaign,
  currencyCode,
  canManageBudget,
  canManageCampaigns,
  canApprove,
  canPublish,
}: {
  campaign: TenantRewardCampaign;
  currencyCode: string;
  canManageBudget: boolean;
  canManageCampaigns: boolean;
  canApprove: boolean;
  canPublish: boolean;
}) {
  const queryClient = useQueryClient();
  const [quantityLimit, setQuantityLimit] = useState(campaign.quantityLimit?.toString() ?? "");
  const [budget, setBudget] = useState(campaign.budgetMinor === null
    ? ""
    : (campaign.budgetMinor / 100).toFixed(2));
  const [formError, setFormError] = useState("");
  const editable = campaign.status === "DRAFT" || campaign.status === "REJECTED";

  useEffect(() => {
    setQuantityLimit(campaign.quantityLimit?.toString() ?? "");
    setBudget(campaign.budgetMinor === null ? "" : (campaign.budgetMinor / 100).toFixed(2));
  }, [campaign.budgetMinor, campaign.quantityLimit]);

  const invalidateCampaignData = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: tenantCampaignExecutionKey }),
      queryClient.invalidateQueries({ queryKey: tenantRewardAuditKey }),
      queryClient.invalidateQueries({ queryKey: tenantRewardCalendarKey }),
      queryClient.invalidateQueries({ queryKey: tenantRewardDashboardKey }),
    ]);
  };

  const limitsMutation = useMutation({
    mutationFn: (input: { quantityLimit: number | null; budgetMinor: number | null }) =>
      updateTenantRewardCampaignExecutionLimits(campaign.publicId, input),
    onSuccess: invalidateCampaignData,
  });
  const transitionMutation = useMutation({
    mutationFn: (target: TenantRewardCampaignStatus) =>
      transitionTenantRewardCampaign(campaign.publicId, target),
    onSuccess: invalidateCampaignData,
  });

  const saveLimits = async (event: SyntheticEvent<HTMLFormElement>) => {
    event.preventDefault();
    setFormError("");
    const parsedQuantity = quantityLimit === "" ? null : Number(quantityLimit);
    const parsedBudget = budget === "" ? null : Math.round(Number(budget) * 100);
    if (parsedQuantity !== null && (!Number.isSafeInteger(parsedQuantity) || parsedQuantity < 1 || parsedQuantity > 100_000_000)) {
      setFormError("Quantity must be a whole number from 1 to 100,000,000, or blank for no cap.");
      return;
    }
    if (parsedBudget !== null && (!Number.isSafeInteger(parsedBudget) || parsedBudget < 1 || parsedBudget > 100_000_000_000)) {
      setFormError("Budget must be positive and no more than 1,000,000,000 in the selected currency.");
      return;
    }
    try {
      await limitsMutation.mutateAsync({ quantityLimit: parsedQuantity, budgetMinor: parsedBudget });
    } catch {
      // React Query exposes the sanitized API error below.
    }
  };

  const moveTo = async (target: TenantRewardCampaignStatus) => {
    setFormError("");
    try {
      await transitionMutation.mutateAsync(target);
    } catch {
      // React Query exposes the sanitized API error below.
    }
  };

  const mutationError = limitsMutation.error ?? transitionMutation.error;
  const targets = campaignLifecycleTargets(campaign.status).filter((target) => {
    if (["APPROVED", "REJECTED"].includes(target)) return canApprove;
    if (["SCHEDULED", "ACTIVE", "PAUSED", "COMPLETED", "ARCHIVED"].includes(target)) return canPublish;
    return canManageCampaigns;
  });

  return <div className="campaign-execution-controls">
    {canManageBudget && <form aria-label={`Execution limits for ${campaign.name}`} onSubmit={(event) => { void saveLimits(event); }}>
      <div className="campaign-execution-controls__heading">
        <div><strong>Execution guardrails</strong><small>Reservations stop automatically at either limit.</small></div>
        {!editable && <span>Locked after approval</span>}
      </div>
      <div className="campaign-execution-limit-grid">
        <label htmlFor={`quantity-${campaign.publicId}`}>
          <span>Quantity limit</span>
          <input
            disabled={!editable || limitsMutation.isPending}
            id={`quantity-${campaign.publicId}`}
            inputMode="numeric"
            max="100000000"
            min="1"
            onChange={(event) => { setQuantityLimit(event.target.value); }}
            placeholder="No cap"
            step="1"
            type="number"
            value={quantityLimit}
          />
        </label>
        <label htmlFor={`budget-${campaign.publicId}`}>
          <span>Budget ({currencyCode})</span>
          <input
            disabled={!editable || limitsMutation.isPending}
            id={`budget-${campaign.publicId}`}
            inputMode="decimal"
            min="0.01"
            onChange={(event) => { setBudget(event.target.value); }}
            placeholder="No cap"
            step="0.01"
            type="number"
            value={budget}
          />
        </label>
      </div>
      <button className="button button--outline button--compact" disabled={!editable || limitsMutation.isPending} type="submit">
        {limitsMutation.isPending ? "Saving limits…" : "Save limits"}
      </button>
    </form>}

    <div className="campaign-lifecycle-actions" aria-label={`Lifecycle actions for ${campaign.name}`}>
      <div><strong>Next lifecycle action</strong><small>Only valid transitions are available.</small></div>
      <div>
        {targets.length > 0 ? targets.map((target) => <button
          aria-label={`Move ${campaign.name} to ${titleCase(target)}`}
          className={target === "ARCHIVED" ? "button button--quiet button--compact" : "button button--outline button--compact"}
          disabled={transitionMutation.isPending}
          key={target}
          onClick={() => { void moveTo(target); }}
          type="button"
        >
          {transitionMutation.isPending && transitionMutation.variables === target
            ? "Updating…"
            : titleCase(target)}
          <ArrowRight aria-hidden="true" />
        </button>) : <span className="campaign-lifecycle-actions__terminal">No further actions</span>}
      </div>
    </div>

    {formError && <AppAlert message={formError} onDismiss={() => { setFormError(""); }} />}
    {mutationError && <AppAlert message={messageFromError(mutationError)} />}
  </div>;
}

function CampaignCard({ campaign, currencyCode, canManageBudget, canManageCampaigns, canApprove, canPublish }: { campaign: TenantRewardCampaign; currencyCode: string; canManageBudget: boolean; canManageCampaigns: boolean; canApprove: boolean; canPublish: boolean }) {
  const metrics = campaignExecutionMetrics(campaign);
  const audience = campaign.segment?.name ?? "All active members";
  const issueDetail = campaign.quantityLimit === null
    ? `${formatNumber(campaign.issuedCount)} issued without a quantity cap`
    : `${formatNumber(campaign.issuedCount)} of ${formatNumber(campaign.quantityLimit)} issued`;
  const budgetDetail = campaign.budgetMinor === null
    ? `${formatMoney(metrics.exposureMinor, currencyCode)} committed or redeemed without a cap`
    : `${formatMoney(metrics.exposureMinor, currencyCode)} of ${formatMoney(campaign.budgetMinor, currencyCode)} exposed`;

  return <article className="campaign-execution-card">
    <header>
      <span className="campaign-execution-card__icon"><Gauge aria-hidden="true" /></span>
      <div><small>{titleCase(campaign.objective)}</small><h3>{campaign.name}</h3></div>
      <StatusBadge status={campaign.status} />
    </header>

    <div className="campaign-execution-context">
      <div><UsersRound aria-hidden="true" /><span><small>Audience</small><strong>{audience}</strong></span></div>
      <div><Gift aria-hidden="true" /><span><small>Reward</small><strong>{campaign.reward?.name ?? "Reward not linked"}</strong></span></div>
      <div><CalendarClock aria-hidden="true" /><span><small>Schedule</small><strong>{formatDate(campaign.startsAt)} – {campaign.endsAt ? formatDate(campaign.endsAt) : "Open end"}</strong></span></div>
      <div><ShieldCheck aria-hidden="true" /><span><small>Audience snapshot</small><strong>{campaign.audienceSnapshottedAt ? formatDate(campaign.audienceSnapshottedAt, true) : "Pending first execution"}</strong></span></div>
    </div>

    <div className="campaign-execution-channels" aria-label="Delivery channels">
      {campaign.channelScope.map((channel) => <span key={channel}>{titleCase(channel)}</span>)}
      <small>{campaign.timezone}</small>
    </div>

    <div className="campaign-execution-metrics">
      <div><Coins aria-hidden="true" /><span><small>Issued</small><strong>{formatNumber(campaign.issuedCount)}</strong></span></div>
      <div><CheckCircle2 aria-hidden="true" /><span><small>Redeemed</small><strong>{formatNumber(campaign.redeemedCount)} · {metrics.redemptionPercent.toFixed(1)}%</strong></span></div>
      <div><WalletCards aria-hidden="true" /><span><small>Committed</small><strong>{formatMoney(campaign.budgetCommittedMinor, currencyCode)}</strong></span></div>
      <div><Activity aria-hidden="true" /><span><small>Budget state</small><strong>{titleCase(campaign.budgetStatus)}</strong></span></div>
    </div>

    <div className="campaign-execution-progress-grid">
      <ExecutionProgress detail={issueDetail} label="Quantity used" value={metrics.issuePercent} />
      <ExecutionProgress detail={budgetDetail} label="Budget exposed" value={metrics.budgetPercent} />
    </div>

    <details className="campaign-execution-details">
      <summary>Execution details</summary>
      <dl>
        <div><dt>Lifecycle version</dt><dd>{formatNumber(campaign.lifecycleVersion)}</dd></div>
        <div><dt>Activated</dt><dd>{formatDate(campaign.activatedAt, true)}</dd></div>
        <div><dt>Completed</dt><dd>{formatDate(campaign.completedAt, true)}</dd></div>
        <div><dt>Budget redeemed</dt><dd>{formatMoney(campaign.budgetRedeemedMinor, currencyCode)}</dd></div>
      </dl>
    </details>

    <CampaignControls campaign={campaign} currencyCode={currencyCode} canManageBudget={canManageBudget} canManageCampaigns={canManageCampaigns} canApprove={canApprove} canPublish={canPublish} />
  </article>;
}

interface ReferralDraft {
  referrerCustomerPublicId: string;
  referredCustomerPublicId: string;
  referrerRewardPublicId: string;
  referredRewardPublicId: string;
  campaignPublicId: string;
  minimumSpendMinor: string;
}

const emptyReferralDraft: ReferralDraft = {
  referrerCustomerPublicId: "",
  referredCustomerPublicId: "",
  referrerRewardPublicId: "",
  referredRewardPublicId: "",
  campaignPublicId: "",
  minimumSpendMinor: "",
};

export function validateReferralDraft(draft: ReferralDraft): string | null {
  if (!draft.referrerCustomerPublicId.trim() || !draft.referredCustomerPublicId.trim()) {
    return "Enter both customer public IDs.";
  }
  if (draft.referrerCustomerPublicId.trim() === draft.referredCustomerPublicId.trim()) {
    return "The referrer and referred customer must be different members.";
  }
  if (!draft.referrerRewardPublicId.trim()) return "Choose or enter the referrer reward public ID.";
  if (!draft.minimumSpendMinor.trim()) return "Enter the minimum qualifying spend in minor units.";
  const spend = Number(draft.minimumSpendMinor);
  if (!Number.isSafeInteger(spend) || spend < 0 || spend > 100_000_000_000) {
    return "Minimum spend must be a whole number from 0 to 100,000,000,000 minor units.";
  }
  return null;
}

function CustomerSelector({
  id,
  label,
  selected,
  excludedCustomerPublicId,
  onSelect,
}: {
  id: string;
  label: string;
  selected: TenantCustomerSummary | null;
  excludedCustomerPublicId: string | undefined;
  onSelect: (customer: TenantCustomerSummary | null) => void;
}) {
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  useEffect(() => {
    const timer = window.setTimeout(() => { setDebouncedSearch(search.trim()); }, 250);
    return () => { window.clearTimeout(timer); };
  }, [search]);

  const customersQuery = useQuery({
    queryKey: ["tenant-customer-search", debouncedSearch],
    queryFn: () => fetchTenantCustomers({
      ...(debouncedSearch ? { q: debouncedSearch } : {}),
      limit: 25,
      offset: 0,
    }),
  });
  const options = useMemo(() => {
    const byId = new Map<string, TenantCustomerSummary>();
    if (selected) byId.set(selected.customerPublicId, selected);
    for (const customer of customersQuery.data ?? []) byId.set(customer.customerPublicId, customer);
    return [...byId.values()];
  }, [customersQuery.data, selected]);

  const choose = (customerPublicId: string) => {
    onSelect(options.find((customer) => customer.customerPublicId === customerPublicId) ?? null);
  };

  return <fieldset className="referral-customer-selector">
    <legend>{label}</legend>
    <label htmlFor={`${id}-search`}><span>Search by name, email, or loyalty number</span>
      <input
        autoComplete="off"
        id={`${id}-search`}
        onChange={(event) => { setSearch(event.target.value); }}
        placeholder="Start typing to narrow customers"
        type="search"
        value={search}
      />
    </label>
    <label htmlFor={`${id}-customer`}><span>Customer</span>
      <select
        id={`${id}-customer`}
        onChange={(event) => { choose(event.target.value); }}
        required
        value={selected?.customerPublicId ?? ""}
      >
        <option value="">Select a customer</option>
        {options.map((customer) => <option
          disabled={customer.customerPublicId === excludedCustomerPublicId || customer.status !== "active"}
          key={customer.customerPublicId}
          value={customer.customerPublicId}
        >
          {memberName(customer.firstName, customer.lastName)} · {customer.email} · {customer.loyaltyNumber}
        </option>)}
      </select>
    </label>
    {customersQuery.isFetching && <small role="status">Searching customers…</small>}
    {customersQuery.isError && <AppAlert message={messageFromError(customersQuery.error)} />}
    {selected && <div className="referral-customer-selector__selected">
      <strong>{memberName(selected.firstName, selected.lastName)}</strong>
      <span>{selected.email}</span>
      <small>{selected.loyaltyNumber} · {formatNumber(selected.pointsBalance)} points</small>
    </div>}
  </fieldset>;
}

function ReferralAttributionForm({ campaigns }: { campaigns: TenantRewardCampaign[] }) {
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState<ReferralDraft>(emptyReferralDraft);
  const [idempotencyKey, setIdempotencyKey] = useState(createIdempotencyKey);
  const [formError, setFormError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [referrerCustomer, setReferrerCustomer] = useState<TenantCustomerSummary | null>(null);
  const [referredCustomer, setReferredCustomer] = useState<TenantCustomerSummary | null>(null);
  const selectedCampaign = campaigns.find((campaign) => campaign.publicId === draft.campaignPublicId) ?? null;

  const mutation = useMutation({
    mutationFn: (input: TenantRewardReferralInput) => createTenantRewardReferral(input),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: tenantReferralKey }),
        queryClient.invalidateQueries({ queryKey: tenantRewardAuditKey }),
      ]);
    },
  });

  const update = (field: keyof ReferralDraft, value: string) => {
    setDraft((current) => ({ ...current, [field]: value }));
  };

  const chooseCampaign = (publicId: string) => {
    const campaign = campaigns.find((item) => item.publicId === publicId);
    setDraft((current) => ({
      ...current,
      campaignPublicId: publicId,
      referrerRewardPublicId: campaign?.reward?.publicId ?? current.referrerRewardPublicId,
    }));
  };

  const submit = async (event: SyntheticEvent<HTMLFormElement>) => {
    event.preventDefault();
    setFormError("");
    setSuccessMessage("");
    const validationError = validateReferralDraft(draft);
    if (validationError) {
      setFormError(validationError);
      return;
    }
    try {
      const referral = await mutation.mutateAsync({
        referrerCustomerPublicId: draft.referrerCustomerPublicId.trim(),
        referredCustomerPublicId: draft.referredCustomerPublicId.trim(),
        referrerRewardPublicId: draft.referrerRewardPublicId.trim(),
        referredRewardPublicId: draft.referredRewardPublicId.trim() || null,
        campaignPublicId: draft.campaignPublicId || null,
        minimumSpendMinor: Number(draft.minimumSpendMinor),
        idempotencyKey,
      });
      setSuccessMessage(`Referral ${referral.publicId} is waiting for its first qualifying checkout.`);
      setDraft(emptyReferralDraft);
      setReferrerCustomer(null);
      setReferredCustomer(null);
      setIdempotencyKey(createIdempotencyKey());
    } catch {
      // React Query exposes the sanitized API error below.
    }
  };

  return <form className="referral-attribution-form" onSubmit={(event) => { void submit(event); }}>
    <div className="referral-attribution-form__heading">
      <div><p className="eyebrow">Immutable attribution</p><h3>Record a referral</h3><p>Rewards issue only after the referred member's first qualifying confirmed checkout.</p></div>
      <Send aria-hidden="true" />
    </div>

    {formError && <AppAlert message={formError} onDismiss={() => { setFormError(""); }} />}
    {mutation.error && <AppAlert message={messageFromError(mutation.error)} />}
    {successMessage && <div className="alert alert--success" role="status">{successMessage}</div>}

    <div className="referral-customer-selector-grid">
      <CustomerSelector
        excludedCustomerPublicId={referredCustomer?.customerPublicId}
        id="referral-referrer"
        label="Referrer"
        onSelect={(customer) => {
          setReferrerCustomer(customer);
          update("referrerCustomerPublicId", customer?.customerPublicId ?? "");
        }}
        selected={referrerCustomer}
      />
      <CustomerSelector
        excludedCustomerPublicId={referrerCustomer?.customerPublicId}
        id="referral-referred"
        label="Referred member"
        onSelect={(customer) => {
          setReferredCustomer(customer);
          update("referredCustomerPublicId", customer?.customerPublicId ?? "");
        }}
        selected={referredCustomer}
      />
    </div>

    <div className="referral-attribution-form__grid">
      <label htmlFor="referral-campaign">
        <span>Campaign <small>(optional)</small></span>
        <select id="referral-campaign" onChange={(event) => { chooseCampaign(event.target.value); }} value={draft.campaignPublicId}>
          <option value="">No linked campaign</option>
          {campaigns.map((campaign) => <option disabled={!campaign.reward} key={campaign.publicId} value={campaign.publicId}>
            {campaign.name}{campaign.reward ? "" : " — reward required"}
          </option>)}
        </select>
      </label>
      <label htmlFor="referral-referrer-reward">
        <span>Referrer reward public ID</span>
        <input
          id="referral-referrer-reward"
          maxLength={120}
          onChange={(event) => { update("referrerRewardPublicId", event.target.value); }}
          readOnly={Boolean(selectedCampaign?.reward)}
          required
          value={draft.referrerRewardPublicId}
        />
      </label>
      <label htmlFor="referral-referred-reward">
        <span>Referred-member reward public ID <small>(optional)</small></span>
        <input id="referral-referred-reward" maxLength={120} onChange={(event) => { update("referredRewardPublicId", event.target.value); }} value={draft.referredRewardPublicId} />
      </label>
      <label htmlFor="referral-minimum-spend">
        <span>Minimum spend (minor currency units)</span>
        <input id="referral-minimum-spend" inputMode="numeric" max="100000000000" min="0" onChange={(event) => { update("minimumSpendMinor", event.target.value); }} required step="1" type="number" value={draft.minimumSpendMinor} />
      </label>
    </div>
    <div className="referral-attribution-form__note"><ShieldCheck aria-hidden="true" /><p>Customer, reward, and campaign references are checked against this Business Owner account. Attribution cannot be edited or deleted after creation.</p></div>
    <button className="button button--primary" disabled={mutation.isPending} type="submit">
      {mutation.isPending ? "Recording referral…" : "Record referral attribution"}
    </button>
  </form>;
}

function ReferralStatusList() {
  const [status, setStatus] = useState<TenantRewardReferralStatus | "ALL">("ALL");
  const referralsQuery = useQuery({
    queryKey: [...tenantReferralKey, status],
    queryFn: () => fetchTenantRewardReferrals({
      ...(status === "ALL" ? {} : { status }),
      limit: 50,
      offset: 0,
    }),
  });

  return <section className="referral-status-panel" aria-labelledby="tenant-referral-status-title">
    <div className="referral-status-panel__heading">
      <div><p className="eyebrow">Qualification queue</p><h3 id="tenant-referral-status-title">Referral status</h3><p>Track attribution, qualifying checkouts, issuance, and capacity errors.</p></div>
      <div>
        <label htmlFor="referral-status-filter">Status</label>
        <select id="referral-status-filter" onChange={(event) => { setStatus(event.target.value as TenantRewardReferralStatus | "ALL"); }} value={status}>
          <option value="ALL">All statuses</option>
          {allReferralStatuses.map((item) => <option key={item} value={item}>{titleCase(item)}</option>)}
        </select>
        <button aria-label="Refresh referral status" className="button button--quiet button--compact" disabled={referralsQuery.isFetching} onClick={() => { void referralsQuery.refetch(); }} type="button"><RefreshCw aria-hidden="true" /> Refresh</button>
      </div>
    </div>

    {referralsQuery.isLoading
      ? <QueryState label="Loading referrals" />
      : referralsQuery.isError
        ? <QueryState error={referralsQuery.error} retry={() => { void referralsQuery.refetch(); }} />
        : referralsQuery.data?.length
          ? <ul className="referral-status-list">{referralsQuery.data.map((referral) => <li key={referral.publicId}>
            <div className="referral-status-list__heading">
              <div><strong>{memberName(referral.referrerFirstName, referral.referrerLastName)}</strong><ArrowRight aria-hidden="true" /><strong>{memberName(referral.referredFirstName, referral.referredLastName)}</strong></div>
              <StatusBadge status={referral.status} />
            </div>
            <p>{referral.campaignName ?? "Unlinked referral"} · {referral.referrerRewardName}</p>
            <dl>
              <div><dt>Minimum spend</dt><dd>{formatMoney(referral.minimumSpendMinor, referral.currencyCode)}</dd></div>
              <div><dt>Attributed</dt><dd>{formatDate(referral.createdAt, true)}</dd></div>
              <div><dt>Qualified</dt><dd>{formatDate(referral.qualifiedAt, true)}</dd></div>
              <div><dt>Rewarded</dt><dd>{formatDate(referral.rewardedAt, true)}</dd></div>
            </dl>
            <div className="referral-status-list__ids">
              <span>Referrer <code>{referral.referrerCustomerPublicId}</code></span>
              <span>Referred <code>{referral.referredCustomerPublicId}</code></span>
            </div>
            {referral.lastErrorCode && <div className="referral-status-list__error" role="status"><AlertTriangle aria-hidden="true" /><span><strong>Action needed</strong>{titleCase(referral.lastErrorCode)}</span></div>}
          </li>)}</ul>
          : <div className="campaign-execution-state campaign-execution-state--empty"><UsersRound aria-hidden="true" /><div><strong>No referrals in this view</strong><p>New referral attributions and their qualification status will appear here.</p></div></div>}
  </section>;
}

export function TenantCampaignExecutionPanel({ currencyCode = "NPR", canManageBudget, canManageCampaigns, canApprove, canPublish }: { currencyCode?: string; canManageBudget: boolean; canManageCampaigns: boolean; canApprove: boolean; canPublish: boolean }) {
  const campaignsQuery = useQuery({
    queryKey: tenantCampaignExecutionKey,
    queryFn: fetchTenantRewardCampaigns,
  });
  const campaigns = campaignsQuery.data ?? [];
  const summary = useMemo(() => ({
    active: campaigns.filter((campaign) => campaign.status === "ACTIVE").length,
    snapshotted: campaigns.filter((campaign) => campaign.audienceSnapshottedAt !== null).length,
    issued: campaigns.reduce((total, campaign) => total + campaign.issuedCount, 0),
    redeemed: campaigns.reduce((total, campaign) => total + campaign.redeemedCount, 0),
  }), [campaigns]);

  return <div className="tenant-campaign-execution-panel">
    <section className="portal-panel campaign-execution-section" aria-labelledby="campaign-execution-title">
      <div className="campaign-execution-section__heading">
        <div><p className="eyebrow">Controlled delivery</p><h2 id="campaign-execution-title">Campaign execution</h2><p>Set hard exposure limits, approve lifecycle changes, and monitor business-safe delivery progress.</p></div>
        <button aria-label="Refresh campaign execution" className="button button--quiet button--compact" disabled={campaignsQuery.isFetching} onClick={() => { void campaignsQuery.refetch(); }} type="button"><RefreshCw aria-hidden="true" /> Refresh</button>
      </div>

      <div className="campaign-execution-summary" aria-label="Campaign execution summary">
        <div><Gauge aria-hidden="true" /><span><small>Active campaigns</small><strong>{formatNumber(summary.active)}</strong></span></div>
        <div><UsersRound aria-hidden="true" /><span><small>Audiences frozen</small><strong>{formatNumber(summary.snapshotted)}</strong></span></div>
        <div><Coins aria-hidden="true" /><span><small>Rewards issued</small><strong>{formatNumber(summary.issued)}</strong></span></div>
        <div><CheckCircle2 aria-hidden="true" /><span><small>Rewards redeemed</small><strong>{formatNumber(summary.redeemed)}</strong></span></div>
      </div>

      {campaignsQuery.isLoading
        ? <QueryState label="Loading campaign execution" />
        : campaignsQuery.isError
          ? <QueryState error={campaignsQuery.error} retry={() => { void campaignsQuery.refetch(); }} />
          : campaigns.length
            ? <div className="campaign-execution-grid">{campaigns.map((campaign) => <CampaignCard campaign={campaign} currencyCode={currencyCode} canManageBudget={canManageBudget} canManageCampaigns={canManageCampaigns} canApprove={canApprove} canPublish={canPublish} key={campaign.publicId} />)}</div>
            : <div className="campaign-execution-state campaign-execution-state--empty"><Gauge aria-hidden="true" /><div><strong>No campaigns yet</strong><p>Create a campaign draft first, then return here to set execution limits and move it through approval.</p></div></div>}
    </section>

    <section className="portal-panel referral-execution-section" aria-labelledby="referral-execution-title">
      <div className="referral-execution-section__heading"><div><p className="eyebrow">Qualified advocacy</p><h2 id="referral-execution-title">Referral rewards</h2><p>Attribute referrals now; the scheduled engine verifies the first eligible checkout and issues each reward once.</p></div><ShieldCheck aria-hidden="true" /></div>
      <div className="referral-execution-layout">
        <ReferralAttributionForm campaigns={campaigns} />
        <ReferralStatusList />
      </div>
    </section>
  </div>;
}
